import { Feature, OrderStatus } from "@enums";
import { ChevronLeft } from "@mui/icons-material";
import { Box, Stack, Typography, useTheme, Button } from "@mui/material";
import BottomActionBar from "@pages/Layout/BottomActionBar";
import {
  OrderOut,
  useWebApiV1GetUserFeatureSwitchesQuery,
  useWebApiV1UpdateOrderMutation,
} from "@providers/hop-ord-server/api";
import { toLocaleDateTimeString } from "@utils";
import dayjs, { Dayjs } from "dayjs";
import { useState } from "react";
import { FormContainer, useForm } from "react-hook-form-mui";
import { DateTimePickerElement } from "react-hook-form-mui/date-pickers";
import { useNavigate } from "react-router-dom";

interface Props {
  order: OrderOut;
}

const HandoverPage = ({ order }: Props) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [updateOrder] = useWebApiV1UpdateOrderMutation();
  const [handoverSubmitted, setHandoverSubmitted] = useState<boolean>(false);
  const now = dayjs();
  const form = useForm<{ handoverDatetime: Dayjs }>({
    defaultValues: {
      handoverDatetime: dayjs(),
    },
    mode: "all",
  });

  const { data: featureSwitchData } = useWebApiV1GetUserFeatureSwitchesQuery();
  const showPlanReview = featureSwitchData?.find(
    (feature) => feature.name === Feature.PLAN_REVIEW,
  );

  const submitHandover = () => {
    const formValues = form.getValues();
    if (!formValues.handoverDatetime) return;

    updateOrder({
      orderId: Number(order.id),
      orderIn: {
        handoverDatetime: formValues.handoverDatetime.toISOString(),
        status: OrderStatus.COMPLETED,
      },
    });
    setHandoverSubmitted(true);
  };

  return (
    <Box
      sx={{
        flex: 1,
      }}
    >
      <Stack
        sx={{
          gap: 2,
        }}
      >
        {order.handoverDatetime ? (
          <>
            <Typography>
              Handover completed successfully on{" "}
              {toLocaleDateTimeString(order.handoverDatetime, "date")} at{" "}
              {toLocaleDateTimeString(order.handoverDatetime, "time")}
            </Typography>

            <Button
              startIcon={<ChevronLeft />}
              sx={{ width: "max-content" }}
              variant="outlined"
              href="/"
            >
              Back to Command Center
            </Button>
          </>
        ) : (
          <>
            <Typography variant="body2">
              By marking this as complete, you confirm that the physician has
              approved the plan and the handover process with the local planning
              team is finished
            </Typography>
            <FormContainer formContext={form}>
              <DateTimePickerElement
                required
                maxDate={now}
                name="handoverDatetime"
                label="Date time completed"
                desktopModeMediaQuery="(min-width:600px)"
                inputProps={{
                  onBlur: () => form.trigger("handoverDatetime"),
                  name: "handoverDatetime",
                }}
                disabled={handoverSubmitted}
                data-testid="form-handover-date-time"
              />
            </FormContainer>
            <Button
              sx={{ width: "max-content" }}
              variant="contained"
              onClick={submitHandover}
              disabled={handoverSubmitted}
            >
              Mark as complete
            </Button>
          </>
        )}
      </Stack>
      <BottomActionBar>
        <Stack
          direction="row"
          sx={{
            width: 1,
            justifyContent: "flex-end",
            padding: 2,
            gap: 2,
            borderTop: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Button
            variant="outlined"
            color="primary"
            onClick={() => {
              if (showPlanReview) {
                navigate(`/commandcenter/${order.id}/plan-review`);
              } else {
                navigate(`/commandcenter/${order.id}/automated`);
              }
            }}
          >
            Back: {showPlanReview ? "Plan Review" : "Plan Generation"}
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => navigate(`/commandcenter`)}
          >
            Return to Command Center
          </Button>
        </Stack>
      </BottomActionBar>
    </Box>
  );
};

export default HandoverPage;
